import { RedocStandalone } from "redoc";
import React, { useEffect, useState } from "react";

import {
  addPaths,
  createMultiProductOpenApiObject,
  createOpenApiObject,
} from "./openapi.algo";
import { fetchAllCustomerDocs } from "../../../api/docs";
import Toast from "../../../Shared/toast/toast";
import { useTitle } from "../../../Shared/useTitle";
import { ACCENTCOLOR } from "../../../Shared/colors";
import { OpenApiDoc, OpenApiTheme } from "./openapi";
import Loading from "../../../Shared/loading/loading";
import { curlconverter } from "../developer-docs/common/curlconverter";

function jumpToRedocHash(hash: string): boolean {
  const decoded = decodeURIComponent(hash);
  const suffixes = [hash, decoded, hash.replace(/^#/, ""), decoded.replace(/^#/, "")];
  for (const suffix of suffixes) {
    const link = document.querySelector(`a[href="${suffix}"], a[href$="${suffix}"]`);
    if (link instanceof HTMLElement) {
      link.click();
      return true;
    }
  }
  const tagOnly = hash.match(/^(#tag\/[^/]+)/);
  if (tagOnly) {
    const tagLink = document.querySelector(
      `a[href="${tagOnly[1]}"], a[href$="${tagOnly[1]}"]`
    );
    if (tagLink instanceof HTMLElement) {
      tagLink.click();
      return true;
    }
  }
  return false;
}

function Docs() {
  useTitle("Documentation | Zoop Customer Platform");
  const [updatedDoc, setUpdatedDoc] = useState<any>(null);

  const curlCon = new curlconverter();
  function toastTimeout(
    showToastValue: boolean,
    toastType: "success" | "error",
    toastMessage: string | string[],
    heading?: string
  ) {
    if (!showToastValue) return;

    // Show toast via react-toastify
    Toast(
      toastType,
      toastMessage,
      heading || (toastType === "success" ? "Success" : "Error")
    );
  }

  const getProductsDocumentation = async () => {
    try {
      const data = await fetchAllCustomerDocs();
      const docs = addPaths(data);

      if (docs.length === 0) {
        setUpdatedDoc({ ...OpenApiDoc });
        if (data.length > 0) {
          console.warn(
            "[customer-docs] Received dev docs but none had a parseable cURL path",
            { received: data.length }
          );
          toastTimeout(
            true,
            "error",
            "Product documentation is not available for your subscriptions yet"
          );
        }
        return;
      }

      if (docs.length < data.length) {
        console.warn("[customer-docs] Skipped dev docs without cURL path", {
          mapped: docs.length,
          received: data.length,
          skipped: data.length - docs.length,
        });
      }

      const docsMap = new Map();
      for (const doc of docs) {
        if (docsMap.get(doc.path) === undefined) {
          docsMap.set(doc.path, [doc]);
        } else {
          docsMap.get(doc.path)?.push(doc);
        }
      }

      const tagGroups = [];
      const paths: Record<string, object> = {};
      const components: Record<string, object> = {};
      const tags: Map<string, string[]> = new Map();

      for (const [key, val] of docsMap) {
        if (!key || !val?.length) continue;

        const hasMultipleProducts = val.length > 1;
        const category = val[0].category || "Other";
        const docType = val[0].type || val[0].name || "API";

        if (tags.get(category) === undefined) {
          tags.set(category, [docType]);
        } else if (!tags.get(category)?.includes(docType)) {
          tags.get(category)?.push(docType);
        }

        if (hasMultipleProducts) {
          const openApiObjs: any = createMultiProductOpenApiObject(val);
          let desc: string = "";
          const curls: any[] = [];
          for (const doc of val) {
            const curl = String(doc.curl ?? "");
            if (!curl) continue;

            desc =
              desc +
              " " +
              String(`\n\n## ${doc?.name?.toUpperCase()} \n${doc.description}`);

            const testCurl = curl.replace("live.", "test.");
            const prodCurl = curl.replace("test.", "live.");

            curls.push({
              lang: doc.name + " TEST cURL",
              source: testCurl,
            });
            curls.push({
              lang: doc.name + " LIVE cURL",
              source: prodCurl,
            });
          }

          paths[key] = {
            parameters: openApiObjs.parameters,
            post: {
              description: desc,
              operationId: String(docType).toLowerCase().replaceAll(" ", "_"),
              tags: [docType],
              summary: docType,
              responses: openApiObjs.responses,
              requestBody: openApiObjs.request_body,
              "x-codeSamples": curls,
            },
          };

          if (openApiObjs.components) {
            components.schemas = {
              ...components.schemas,
              ...openApiObjs.components.schemas,
            };
          }
        } else {
          const openApiObj = createOpenApiObject(
            val[0].arn,
            val[0].path,
            val[0].name,
            val[0].description,
            val[0].type,
            val[0].category,
            val[0].curl,
            val[0].headers,
            val[0].params,
            val[0].responses
          );

          const testCurl = openApiObj.curl.replace("live.", "test.");
          const prodCurl = openApiObj.curl.replace("test.", "live.");

          const operationId = openApiObj.name
            .toLowerCase()
            .replaceAll(" ", "_");
          paths[key] = {
            parameters: openApiObj.parameters,
            post: {
              description: openApiObj.description,
              operationId: operationId,
              tags: [openApiObj.tag],
              summary: openApiObj.name,
              responses: openApiObj.responses,
              requestBody: openApiObj.request_body,
              "x-codeSamples": [
                {
                  lang: "TEST cURL",
                  source: testCurl,
                },
                {
                  lang: "LIVE cURL",
                  source: prodCurl,
                },
                {
                  lang: "Python",
                  source: curlCon.toPython(openApiObj.curl, "live"),
                },
                {
                  lang: "Java",
                  source: curlCon.toJava(openApiObj.curl, "live"),
                },
                {
                  lang: "JavaScript",
                  source: curlCon.toJavascript(openApiObj.curl, "live"),
                },
                {
                  lang: "C#",
                  source: curlCon.toCSharp(openApiObj.curl, "live"),
                },
                {
                  lang: "PHP",
                  source: curlCon.toPHP(openApiObj.curl, "live"),
                },
                {
                  lang: "Go",
                  source: curlCon.toGo(openApiObj.curl, "live"),
                },
                {
                  lang: "R",
                  source: curlCon.toR(openApiObj.curl, "live"),
                },
              ],
            },
          };

          if (openApiObj.components) {
            components.schemas = {
              ...components.schemas,
              ...openApiObj.components.schemas,
            };
          }
        }
      }

      for (const [key, val] of tags) {
        tagGroups.push({
          name: key,
          tags: val,
        });
      }

      setUpdatedDoc({
        ...OpenApiDoc,
        "x-tagGroups": tagGroups,
        paths: paths,
        components: components,
      });
    } catch (error) {
      setUpdatedDoc({
        ...OpenApiDoc,
      });

      console.error(error);

      toastTimeout(
        true,
        "error",
        "Failed to build product documentation from API data"
      );
    }
  };

  useEffect(() => {
    getProductsDocumentation();
  }, []);

  useEffect(() => {
    if (!updatedDoc) return;
    const hash = window.location.hash;
    if (!hash.startsWith("#tag/")) return;

    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      if (jumpToRedocHash(hash) || tries >= 40) {
        window.clearInterval(timer);
      }
    }, 100);

    return () => window.clearInterval(timer);
  }, [updatedDoc]);

  return (
    <div className="w-100 h-100">
      {updatedDoc ? (
        <RedocStandalone
          spec={updatedDoc}
          options={{
            expandResponses: "200",
            nativeScrollbars: true,
            hideDownloadButton: true,
            sortTagsAlphabetically: true,
            sortPropsAlphabetically: true,
            sortOperationsAlphabetically: true,
            theme: OpenApiTheme,
          }}
        />
      ) : (
        <div
          style={{ height: "100vh" }}
          className="d-flex align-items-center justify-content-center"
        >
          <Loading loadingColor={ACCENTCOLOR} />
        </div>
      )}
    </div>
  );
}

export default Docs;
